import React from 'react';

import { useEvent } from '@legacy-providers/event';

import { Data } from '@shared/api/types';
import { getCrewMemberOptions } from '@shared/crew-members/endpoints/getCrewMemberOptions';
import { getCrewMemberOptionsForRegistration } from '@shared/crew-members/endpoints/getCrewMemberOptionsForRegistration';
import { UnclaimParkingStickerButton } from '@shared/parking-requests/components/UnclaimParkingStickerButton';
import { getRegistrationParkingOptions } from '@shared/parking-requests/endpoints/getRegistrationParkingOptions';
import { HandOutParkingStickerButton } from '@shared/parking-stickers/components/HandOutParkingStickerButton';
import { getEventParkingOptions } from '@shared/parkings/endpoints/getEventParkingOptions';
import { CrewMemberCell } from '@shared/ui/components/CrewMemberCell';
import { DataTable } from '@shared/ui/components/DataTable';

type ParkingRequestParkingSticker = App.Domain.Parking.Data.ParkingRequestParkingStickerData;

type Columns = 'crewMember' | 'parking' | 'sticker' | 'redeemed' | 'code';

type ParkingRequestsDataTableProps = {
  parkingStickers: Data<ParkingRequestParkingSticker>;
  registrationId?: number;
  columns?: Partial<{ [k in Columns]: boolean }>;
  avoidPagination?: boolean;
  withoutFilters?: boolean;
};

export function ParkingStickersDataTable({
  parkingStickers,
  registrationId,
  columns = {},
  avoidPagination = false,
  withoutFilters = false,
}: ParkingRequestsDataTableProps) {
  const event = useEvent();

  return (
    <DataTable<ParkingRequestParkingSticker>
      data={parkingStickers}
      columns={[
        {
          visible: columns?.crewMember !== false,
          name: 'parkingRequest.crewMember.user.fullName',
          label: 'Request by Crew Member',
          filterable: {
            name: 'parkingRequest.crew_member_id',
            label: 'Crew Member',
            icon: 'user',
            type: 'select',
            options: (query) =>
              registrationId ? getCrewMemberOptionsForRegistration(registrationId, query) : getCrewMemberOptions(query),
          },
          sortable: false,
          render: (row) => <CrewMemberCell crewMember={row.parkingRequest!.crewMember!} showCompany={false} />,
        },
        {
          name: 'claimedByCrewMember.user.fullName',
          label: 'Claimed by Crew Member',
          filterable: {
            name: 'claimed_by_crew_member_id',
            label: 'Claimed by Crew Member',
            icon: 'user',
            type: 'select',
            options: (query) =>
              registrationId ? getCrewMemberOptionsForRegistration(registrationId, query) : getCrewMemberOptions(query),
          },
          sortable: false,
          render: (row) =>
            row.claimedByCrewMember && <CrewMemberCell crewMember={row.claimedByCrewMember!} showCompany={false} />,
        },
        {
          visible: columns?.parking !== false,
          name: 'parkingRequest.parking.name',
          label: 'Parking',
          filterable: {
            name: 'parkingRequest.parking_id',
            label: 'Parking',
            icon: 'car',
            type: 'select',
            options: (query) =>
              registrationId
                ? getRegistrationParkingOptions(registrationId, query)
                : getEventParkingOptions(event.id, query),
          },
          sortable: false,
          render: (row) => row.parkingRequest!.parking.name,
        },
        {
          visible: columns?.sticker !== false,
          name: 'parkingSticker.name',
          label: 'Sticker',
          filterable: false,
          sortable: false,
          render: (row) => row.parkingSticker!.name,
        },
        {
          name: 'handledByUser.fullName',
          label: 'Handled by',
          sortable: false,
          render: (row) => row.handledByUser?.fullName,
        },
        {
          label: 'Claimed',
          name: 'claimedAt',
          type: 'datetime',
          filterable: {
            type: 'select',
            name: 'claimed',
            options: [
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' },
            ],
          },
        },
        {
          label: 'Code',
          name: 'code',
          type: 'number',
        },
        {
          type: 'actions',
          render: (row) => (
            <div className="flex flex-row items-center justify-end gap-2">
              <HandOutParkingStickerButton parkingRequestParkingSticker={row} onHandOut={parkingStickers.refresh} />
              <UnclaimParkingStickerButton parkingRequestParkingSticker={row} onUnclaim={parkingStickers.refresh} />
            </div>
          ),
        },
      ]}
      avoidPagination={avoidPagination}
      withoutFilters={withoutFilters}
    />
  );
}
